import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useConvexSubscriptionSync } from '../hooks/useConvexPayments';
import { useConvexCommitSettings, useConvexDashboard } from '../hooks/useConvexGitHub';
import { setCommitSettings } from '../store/slices/githubSlice';
import StatusCard from '../components/ui/StatusCard';
import SettingsCard from '../components/ui/SettingsCard';
import ProgressBar from '../components/ui/ProgressBar';
import AchievementBadge from '../components/ui/AchievementBadge';
import PolarCheckout from '../components/PolarCheckout';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const dispatch = useDispatch();
  
  // Use Convex subscription sync hook instead of legacy REST
  useConvexSubscriptionSync(true);
  
  // Use Convex hooks
  const { saveCommitSettings, loading: commitSettingsLoading, commitSettings: convexCommitSettings } = useConvexCommitSettings();
  const { streakProgress: convexStreakProgress } = useConvexDashboard();
  
  const { connected: githubConnected } = useSelector((state) => state.github);
  const { commitSettings } = useSelector((state) => state.github);
  const { has_access: hasAccess, plan_type: planType, plan_name: planName } = useSelector((state) => state.subscription);

  // Sync Convex commit settings into Redux so UI always shows saved values
  useEffect(() => {
    if (convexCommitSettings && !convexCommitSettings.loading) {
      dispatch(setCommitSettings({
        commits_per_day: convexCommitSettings.commits_per_day || 1,
        random_commits: convexCommitSettings.random_commits || false,
      }));
    }
  }, [convexCommitSettings?.commits_per_day, convexCommitSettings?.random_commits, dispatch]);

  // Use convexStreakProgress as single source of truth for all streak data
  const currentStreak = convexStreakProgress?.currentStreak || 0;
  const longestStreak = convexStreakProgress?.longestStreak || 0;
  const totalCommits = convexStreakProgress?.totalCommits || 0;
  const streakPercentage = convexStreakProgress?.streakPercentage || 0;
  const daysRemaining = convexStreakProgress?.days_remaining || (planType === 'yearly' ? 365 : 30);
  const streakGoal = planType === 'yearly' ? 365 : 30;

  const handleSaveCommitSettings = async () => {
    const result = await saveCommitSettings(commitSettings);
    if (result) {
      toast.success('✅ Settings saved! Automation will use these settings from tomorrow.');
    }
  };

  const handleCommitSettingChange = (field, value) => {
    dispatch(setCommitSettings({ ...commitSettings, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header with Quick Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="mb-6 lg:mb-0">
            <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-full text-sm font-medium text-blue-700 mb-4">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Your Dashboard
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
              Welcome back, developer! 👋
            </h1>
            <p className="text-gray-600">Here's your GitHub streak progress and activity</p>
          </div>
        </div>

        <div className="space-y-8">

      {/* Enhanced Status Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <StatusCard
          title="GitHub Status"
          value={githubConnected ? "Connected" : "Not Connected"}
          status={githubConnected ? "success" : "error"}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          }
          description={githubConnected ? "GitHub integration is active" : "Connect your GitHub account"}
          gradient={true}
          size="lg"
        />

        {/* Current Streak - reads live from Convex */}
        <StatusCard
          title="Current Streak"
          value={currentStreak > 0 ? `${currentStreak} days 🔥` : '0 days'}
          status={currentStreak > 0 ? "success" : "default"}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
            </svg>
          }
          description={currentStreak > 0 ? `Best: ${longestStreak} days` : 'Keep the streak alive!'}
          gradient={true}
          size="lg"
        />

        <StatusCard
          title="Commits Per Day"
          value={commitSettings.random_commits ? 'Random (1-5)' : (commitSettings.commits_per_day || 1)}
          status="info"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          description="Daily automation frequency"
          gradient={true}
          size="lg"
        />
      </div>

      {/* Enhanced Commit Settings */}
      <SettingsCard
        title="Commit Settings"
        description="Configure your automated commit frequency and behavior"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        disabled={!hasAccess}
        gradient={true}
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Commits Per Day (Maximum 5)
            </label>
            <select
              value={commitSettings.commits_per_day || 1}
              onChange={(e) => handleCommitSettingChange('commits_per_day', parseInt(e.target.value))}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
              disabled={!hasAccess}
            >
              <option value={1}>1 commit per day</option>
              <option value={2}>2 commits per day</option>
              <option value={3}>3 commits per day</option>
              <option value={4}>4 commits per day</option>
              <option value={5}>5 commits per day</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="random_commits"
              checked={commitSettings.random_commits || false}
              onChange={(e) => handleCommitSettingChange('random_commits', e.target.checked)}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-300"
              disabled={!hasAccess}
            />
            <label htmlFor="random_commits" className="text-sm font-medium text-gray-700">
              Enable random commits (1-5 per day for natural variation)
            </label>
          </div>
          
          {/* Save button only */}
          <div className="flex">
            <button
              onClick={handleSaveCommitSettings}
              disabled={!hasAccess || commitSettingsLoading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 flex items-center"
            >
              {commitSettingsLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Settings
                </>
              )}
            </button>
          </div>

          {/* Automation info banner */}
          {hasAccess && githubConnected && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-green-800 mb-1">Automation Active</h4>
                  <p className="text-sm text-green-700">
                    StreakKeeper automatically commits to your GitHub every day at <strong>10:00 AM UTC</strong> using the settings above. 
                    Save your settings to update the commit frequency.
                  </p>
                </div>
              </div>
            </div>
          )}

          {hasAccess && !githubConnected && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-amber-800 mb-1">GitHub Not Connected</h4>
                  <p className="text-sm text-amber-700">
                    Go to <strong>Settings</strong> to connect your GitHub Personal Access Token before automation can start.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!hasAccess && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-1">Subscription Required</h4>
                  <p className="text-sm text-yellow-700">
                    Subscribe to a plan to access commit settings and GitHub integration.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </SettingsCard>

      {/* Enhanced Streak Progress */}
      <SettingsCard
        title="Streak Progress"
        description="Track your GitHub streak performance and milestones"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        }
        gradient={true}
      >
        {/* Plan Badge + Key Stats Header */}
        <div className="flex flex-wrap justify-between items-start mb-6 gap-3">
          <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-medium">
            {planName || 'Monthly Plan'}
          </div>
          <div className="flex gap-4 text-sm">
            <span className="text-gray-500">Goal: <span className="font-semibold text-gray-800">{streakGoal} days</span></span>
            <span className="text-gray-500">Remaining: <span className="font-semibold text-gray-800">{daysRemaining} days</span></span>
          </div>
        </div>
        
        {planType === 'yearly' ? (
          // YEARLY SUBSCRIBER - Enhanced Premium Features
          <div className="space-y-8">
            {/* Premium Stats Grid */}
            <div className="grid md:grid-cols-4 gap-5">
              <div className="text-center p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50">
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                  {currentStreak}
                </div>
                <div className="text-gray-600 text-xs font-medium uppercase tracking-wide">Current Streak</div>
              </div>
              
              <div className="text-center p-5 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-200/50">
                <div className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent mb-1">
                  {longestStreak}
                </div>
                <div className="text-gray-600 text-xs font-medium uppercase tracking-wide">Longest Streak</div>
              </div>
              
              <div className="text-center p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200/50">
                <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                  {totalCommits}
                </div>
                <div className="text-gray-600 text-xs font-medium uppercase tracking-wide">Total Commits</div>
              </div>
              
              <div className="text-center p-5 bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl border border-purple-200/50">
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-1">
                  {streakPercentage}%
                </div>
                <div className="text-gray-600 text-xs font-medium uppercase tracking-wide">Yearly Progress</div>
              </div>
            </div>

            {/* Premium Progress Bars */}
            <div className="space-y-5">
              <ProgressBar
                progress={streakPercentage}
                gradient="from-blue-500 to-purple-500"
                label={`Progress toward 365-day yearly goal (${currentStreak} / 365 days)`}
                height="h-4"
              />
              
              {/* Monthly milestone within the year */}
              <ProgressBar
                progress={Math.min(100, Math.round(((currentStreak % 30) / 30) * 100))}
                gradient="from-green-500 to-teal-500"
                label={`Current month milestone (${currentStreak % 30} / 30 days)`}
                height="h-3"
              />
            </div>

            {/* Premium Insights */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl border border-indigo-200/50">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Premium Analytics</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Current streak: <span className="font-medium text-gray-800">{currentStreak} days</span></p>
                    <p>Best streak: <span className="font-medium text-gray-800">{longestStreak} days</span></p>
                    <p>Total commits via StreakKeeper: <span className="font-medium text-gray-800">{totalCommits}</span></p>
                    <p>Days remaining to goal: <span className="font-medium text-gray-800">{daysRemaining} days</span></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievement Badges */}
            <div className="flex flex-wrap gap-3">
              <AchievementBadge
                icon="🏆"
                title="30-Day Achiever"
                earned={currentStreak >= 30}
                gradient="from-yellow-400 to-orange-500"
              />
              <AchievementBadge
                icon="💎"
                title="Century Club"
                earned={currentStreak >= 100}
                gradient="from-purple-400 to-pink-500"
              />
              <AchievementBadge
                icon="👑"
                title="Yearly Champion"
                earned={currentStreak >= 365}
                gradient="from-yellow-400 to-orange-500"
              />
            </div>
          </div>
        ) : (
          // MONTHLY SUBSCRIBER - Standard Features
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-5">
              <div className="text-center p-7 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50">
                <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                  {currentStreak}
                </div>
                <div className="text-gray-600 font-medium">Current Streak Days</div>
                {currentStreak > 0 && (
                  <div className="text-2xl mt-1">🔥</div>
                )}
              </div>
              
              <div className="text-center p-7 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-200/50">
                <div className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent mb-2">
                  {longestStreak}
                </div>
                <div className="text-gray-600 font-medium">Longest Streak</div>
              </div>

              <div className="text-center p-7 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200/50">
                <div className="text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                  {totalCommits}
                </div>
                <div className="text-gray-600 font-medium">Total Commits Made</div>
              </div>
            </div>

            <ProgressBar
              progress={streakPercentage}
              gradient="from-blue-600 to-indigo-600"
              label={`Progress toward 30-day goal (${currentStreak} / 30 days)`}
              height="h-3"
            />

            {/* Upgrade CTA for Monthly Users */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200/50">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Unlock Yearly Analytics</h4>
                  <p className="text-sm text-gray-600">
                    Get 365-day tracking, premium insights, and achievement badges
                  </p>
                </div>
                <PolarCheckout planType="yearly">
                  <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5">
                    Upgrade to Yearly
                  </button>
                </PolarCheckout>
              </div>
            </div>
          </div>
        )}
      </SettingsCard>
    </div>
  </div>
</div>
  );
};

export default DashboardPage;
